import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';

export const getPasswordRequirements = (password = '') => [
    { label: 'Almeno 12 caratteri', valid: password.length >= 12 },
    { label: 'Una lettera maiuscola', valid: /[A-Z]/.test(password) },
    { label: 'Una lettera minuscola', valid: /[a-z]/.test(password) },
    { label: 'Un numero', valid: /[0-9]/.test(password) },
    {
        label: 'Un carattere speciale',
        valid: /[!@#$%^&*()_\-+={}[\]:;"'<>,.?/\\|]/.test(password),
    },
];

const PasswordEditor = ({
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    onSubmit,
    submitting = false,
    contextValid = true,
    submitLabel = 'Conferma nuova password',
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const requirements = useMemo(
        () => getPasswordRequirements(password),
        [password]
    );
    const passwordIsValid = requirements.every(({ valid }) => valid);
    const passwordsMatch =
        confirmPassword.length > 0 && password === confirmPassword;
    const canSubmit =
        contextValid && passwordIsValid && passwordsMatch && !submitting;

    return (
        <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
                <TextField
                    label="Nuova password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    fullWidth
                    autoFocus
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() =>
                                        setShowPassword((value) => !value)
                                    }
                                    edge="end"
                                    aria-label={
                                        showPassword
                                            ? 'Nascondi password'
                                            : 'Mostra password'
                                    }
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    label="Conferma password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    fullWidth
                    autoComplete="new-password"
                    error={confirmPassword.length > 0 && !passwordsMatch}
                    helperText={
                        confirmPassword.length > 0 && !passwordsMatch
                            ? 'Le password non coincidono'
                            : ' '
                    }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() =>
                                        setShowConfirmPassword(
                                            (value) => !value
                                        )
                                    }
                                    edge="end"
                                    aria-label={
                                        showConfirmPassword
                                            ? 'Nascondi conferma password'
                                            : 'Mostra conferma password'
                                    }
                                >
                                    {showConfirmPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            <Box
                sx={{
                    mt: 1,
                    mb: 3,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#f7fafb',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mb: 1,
                        fontWeight: 700,
                        color: 'text.secondary',
                    }}
                >
                    La password deve contenere:
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 0.75,
                    }}
                >
                    {requirements.map((requirement) => (
                        <Stack
                            key={requirement.label}
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                        >
                            <CheckCircle
                                sx={{
                                    fontSize: 16,
                                    color: requirement.valid
                                        ? 'success.main'
                                        : 'grey.400',
                                }}
                            />
                            <Typography
                                variant="caption"
                                color={
                                    requirement.valid
                                        ? 'text.primary'
                                        : 'text.secondary'
                                }
                            >
                                {requirement.label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Box>

            <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                disabled={!canSubmit}
                sx={{ py: 1.4, bgcolor: '#11526f', fontWeight: 700 }}
            >
                {submitting ? 'Salvataggio in corso...' : submitLabel}
            </Button>
        </Box>
    );
};

export default PasswordEditor;
